import { SessionLeafNode } from '@biconomy/modules/dist/src/interfaces/ISessionStorage'
import { Period } from '@kwenta/sdk/constants'
import { SwapDepositToken } from '@kwenta/sdk/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DEFAULT_FUTURES_MARGIN_TYPE } from 'constants/futures'
import {
	DEFAULT_MAP_BY_NETWORK,
	DEFAULT_QUERY_STATUS,
	LOADING_STATUS,
	SUCCESS_STATUS,
} from 'state/constants'
import { FetchStatus } from 'state/types'

import {
	fetchDelegatesForAccount,
	fetchPositionHistoryForTrader,
	fetchSubAccountsForAccount,
} from './actions'
import { AppFuturesMarginType } from './common/types'
import {
	InputCurrencyDenomination,
	FuturesState,
	TradingModes,
	DelegationAccountInfo,
	ManageModalType,
} from './types'

export const FUTURES_INITIAL_STATE: FuturesState = {
	selectedType: DEFAULT_FUTURES_MARGIN_TYPE,
	confirmationModalOpen: false,
	selectedManageModalType: ManageModalType.Bridge,
	selectedInputDenomination: 'usd',
	selectedInputHours: 1,
	selectedChart: 'price',
	selectedSwapDepositToken: SwapDepositToken.SUSD,
	preferences: {
		showHistory: true,
		tradingMode: TradingModes.DEFAULT,
	},
	oneClickTrading: {
		accountBalance: '0',
		accountDelegated: false,
		sessionInfo: undefined,
	},
	dashboard: {
		selectedPortfolioTimeframe: Period.ONE_WEEK,
	},
	leaderboard: {
		selectedTrader: undefined,
		selectedTraderPositionHistory: DEFAULT_MAP_BY_NETWORK,
	},
	tradePanelDrawerOpen: false,
	historicalFundingRatePeriod: Period.TWO_WEEKS,
	queryStatuses: {
		selectedTraderPositionHistory: DEFAULT_QUERY_STATUS,
		selectedTraderDelegatesList: DEFAULT_QUERY_STATUS,
		selectedTraderSubAccountsList: DEFAULT_QUERY_STATUS,
	},
	delegation: {
		selectedDelegationAddress: '',
		selectedSubAccountAddress: '',
		newNickname: '',
		delegatesList: [],
		subAccountList: [],
		addressBook: {},
	},
}

const futuresSlice = createSlice({
	name: 'futures',
	initialState: FUTURES_INITIAL_STATE,
	reducers: {
		setFuturesAccountType: (state, action: PayloadAction<AppFuturesMarginType>) => {
			state.selectedType = action.payload
		},
		setSelectedInputDenomination: (state, action: PayloadAction<InputCurrencyDenomination>) => {
			state.selectedInputDenomination = action.payload
		},
		setSelectedInputFundingRateHour: (state, action: PayloadAction<number>) => {
			state.selectedInputHours = action.payload
		},
		setSelectedTrader: (
			state,
			action: PayloadAction<{ trader: string; traderEns: string | undefined | null } | undefined>
		) => {
			state.leaderboard.selectedTrader = action.payload
		},
		setSelectedPortfolioTimeframe: (state, action: PayloadAction<Period>) => {
			state.dashboard.selectedPortfolioTimeframe = action.payload
		},
		setTradePanelDrawerOpen: (state, action: PayloadAction<boolean>) => {
			state.tradePanelDrawerOpen = action.payload
		},
		toggleShowTradeHistory: (state) => {
			state.preferences.showHistory = !state.preferences.showHistory
		},
		setTradingMode: (state, action: PayloadAction<TradingModes>) => {
			state.preferences.tradingMode = action.payload
		},
		setAbstractionBalance: (state, action: PayloadAction<string>) => {
			state.oneClickTrading.accountBalance = action.payload
		},
		setDelegated: (state, action: PayloadAction<boolean>) => {
			state.oneClickTrading.accountDelegated = action.payload
		},
		setSession: (state, action: PayloadAction<SessionLeafNode | undefined>) => {
			state.oneClickTrading.sessionInfo = action.payload
		},
		setSelectedChart: (state, action: PayloadAction<'price' | 'funding'>) => {
			state.selectedChart = action.payload
		},
		setHistoricalFundingRatePeriod: (state, action: PayloadAction<Period>) => {
			state.historicalFundingRatePeriod = action.payload
		},
		setSelectedSwapDepositToken: (state, action: PayloadAction<SwapDepositToken>) => {
			state.selectedSwapDepositToken = action.payload
		},
		setSelectedDelegationAddress: (state, action: PayloadAction<string>) => {
			state.delegation.selectedDelegationAddress = action.payload
		},
		setSelectedSubAccountAddress: (state, action: PayloadAction<string>) => {
			state.delegation.selectedSubAccountAddress = action.payload
		},
		addDelegateToDelegatesList: (state, action: PayloadAction<DelegationAccountInfo>) => {
			const addressExists = state.delegation.delegatesList.some(
				(delegate) => delegate.address.toLowerCase() === action.payload.address.toLowerCase()
			)
			if (!addressExists) {
				state.delegation.delegatesList.push(action.payload)
			}
		},
		removeDelegateFromDelegatesList: (state, action: PayloadAction<string>) => {
			state.delegation.delegatesList = state.delegation.delegatesList.filter(
				(delegate) => delegate.address.toLowerCase() !== action.payload
			)
		},
		addDelegateToAddressBook: (
			state,
			action: PayloadAction<{ wallet: string; delegate: DelegationAccountInfo }>
		) => {
			const { wallet, delegate } = action.payload
			if (state.delegation.addressBook[wallet]) {
				const addressExists = state.delegation.addressBook[wallet].some(
					({ address }) => address.toLowerCase() === delegate.address.toLowerCase()
				)
				if (!addressExists) {
					state.delegation.addressBook[wallet].push(delegate)
				}
			} else {
				state.delegation.addressBook[wallet] = [delegate]
			}
		},
		removeDelegateFromAddressBook: (
			state,
			action: PayloadAction<{ wallet: string; delegateAddress: string }>
		) => {
			const { wallet, delegateAddress } = action.payload
			if (state.delegation.addressBook[wallet]) {
				state.delegation.addressBook[wallet] = state.delegation.addressBook[wallet].filter(
					({ address }) => address.toLowerCase() !== delegateAddress.toLowerCase()
				)
			}
		},
		renameAddress: (
			state,
			action: PayloadAction<{ wallet: string; delegate: DelegationAccountInfo }>
		) => {
			const { wallet, delegate } = action.payload
			if (state.delegation.addressBook[wallet]) {
				const existingDelegateIndex = state.delegation.addressBook[wallet].findIndex(
					({ address }) => address.toLowerCase() === delegate.address.toLowerCase()
				)

				if (existingDelegateIndex !== -1) {
					state.delegation.addressBook[wallet][existingDelegateIndex].nickname = delegate.nickname
				} else {
					state.delegation.addressBook[wallet].push(delegate)
				}
			} else {
				state.delegation.addressBook[wallet] = [delegate]
			}
		},
		setNewNickname: (state, action: PayloadAction<string>) => {
			state.delegation.newNickname = action.payload
		},
		setSelectedManageModalType: (state, action: PayloadAction<ManageModalType>) => {
			state.selectedManageModalType = action.payload
		},
	},

	extraReducers(builder) {
		// Fetch position history for trader
		builder.addCase(fetchPositionHistoryForTrader.pending, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = LOADING_STATUS
		})
		builder.addCase(fetchPositionHistoryForTrader.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = SUCCESS_STATUS
			if (!payload) return
			futuresState.leaderboard.selectedTraderPositionHistory[payload.networkId] = {
				...futuresState.leaderboard.selectedTraderPositionHistory[payload.networkId],
				[payload.address]: payload.history,
			}
		})
		builder.addCase(fetchPositionHistoryForTrader.rejected, (futuresState) => {
			futuresState.queryStatuses.selectedTraderPositionHistory = {
				error: 'Failed to fetch traders position history',
				status: FetchStatus.Error,
			}
		})

		// manage delegates
		builder.addCase(fetchDelegatesForAccount.pending, (futuresState) => {
			futuresState.queryStatuses.selectedTraderDelegatesList = LOADING_STATUS
		})
		builder.addCase(fetchDelegatesForAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.selectedTraderDelegatesList = SUCCESS_STATUS
			if (payload) {
				futuresState.delegation.delegatesList = payload.delegatesWithNicknames
			}
		})
		builder.addCase(fetchDelegatesForAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.selectedTraderDelegatesList = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})

		// manage sub-accounts
		builder.addCase(fetchSubAccountsForAccount.pending, (futuresState) => {
			futuresState.queryStatuses.selectedTraderSubAccountsList = LOADING_STATUS
		})
		builder.addCase(fetchSubAccountsForAccount.fulfilled, (futuresState, { payload }) => {
			futuresState.queryStatuses.selectedTraderSubAccountsList = SUCCESS_STATUS
			if (payload) {
				futuresState.delegation.subAccountList = payload.subAccountsWithNicknames
			} else {
				futuresState.delegation.subAccountList = []
			}
		})
		builder.addCase(fetchSubAccountsForAccount.rejected, (futuresState) => {
			futuresState.queryStatuses.selectedTraderSubAccountsList = {
				status: FetchStatus.Error,
				error: 'Failed to fetch delegates',
			}
		})
	},
})

export default futuresSlice.reducer

export const {
	setFuturesAccountType,
	setSelectedTrader,
	setSelectedInputDenomination,
	setSelectedInputFundingRateHour,
	setSelectedPortfolioTimeframe,
	setTradePanelDrawerOpen,
	toggleShowTradeHistory,
	setAbstractionBalance,
	setDelegated,
	setSession,
	setTradingMode,
	setSelectedChart,
	setHistoricalFundingRatePeriod,
	setSelectedSwapDepositToken,
	setSelectedDelegationAddress,
	setSelectedSubAccountAddress,
	addDelegateToAddressBook,
	removeDelegateFromAddressBook,
	addDelegateToDelegatesList,
	removeDelegateFromDelegatesList,
	renameAddress,
	setNewNickname,
	setSelectedManageModalType,
} = futuresSlice.actions
